/**
 * kDocChartSvgAxes - kDocChartSvg's extent, ticks, frame, grid, category and bar-label placement members.
 * Split out of kDocChartSvgCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kDocChartSvg keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreHandler";
import type { kDocChartSvg } from "./kDocChartSvgCore";
import { kDocChartFrame, kDocChartLabel, kDocChartSvgMath } from "./kDocChartSvgMath";

// 2026-10-22 (owner: no runtime import cycles): this module no longer imports kDocChartSvgCore at runtime - the pure
// statics it called there live in the leaf kDocChartSvgMath, and the `Self: typeof kDocChartSvg` members below are
// same-signature delegates to it.

export class kDocChartSvgAxes {
    /** A number for an SVG attribute (at most 2 decimals, "." as the separator). */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Num (it owns the gate and handler label).
    public static Num = (Self: typeof kDocChartSvg, Value: number): string => { return kDocChartSvgMath.Num(Value); };

    /** XML text escaping. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Escape (it owns the gate and handler label).
    public static Escape = (Self: typeof kDocChartSvg, Text: string): string => { return kDocChartSvgMath.Escape(Text); };

    // ---- the value scale ---------------------------------------------------------------------------------------

    /** The smallest and largest value drawn (stacked: the stacks' sums), always including 0. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Extent - its gate, try/catch and handler label "kDocChartSvg.Extent" stay verbatim, so error reports are unchanged.
    public static Extent = (Self: kDocChartSvg): number[] => {
        if (getCoreUtils().KErrorMode) { return [0, 1]; }
        try {
            let Min: number = 0;
            let Max: number = 0;
            const Stacked: boolean = Self["_Chart"].Kind === "stacked";
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index++) {
                let Up: number = 0;
                let Down: number = 0;
                for (const Series of Self["_Chart"].Series) {
                    const Value: number | null = Series.Values[Index];
                    if (Value === null || Value === undefined || isNaN(Value)) { continue; }
                    if (Stacked === false) { Min = Math.min(Min, Value); Max = Math.max(Max, Value); continue; }
                    if (Value >= 0) { Up = Up + Value; } else { Down = Down + Value; }
                }
                Min = Math.min(Min, Down);
                Max = Math.max(Max, Up);
            }
            if (Max === Min) { Max = Min + 1; }
            return [Min, Max];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Extent");
            return [0, 1];
        }
    };

    /**
     * "Nice" axis ticks (steps of 1, 2 or 5 x 10^n, about Count of them) covering Min..Max. `MinStep` (default 0 =
     * none): the smallest step allowed - 1 keeps a count's axis on whole numbers.
     */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Ticks (it owns the gate and handler label).
    public static Ticks = (Self: typeof kDocChartSvg, Min: number, Max: number, Count: number, MinStep: number = 0): number[] => {
        return kDocChartSvgMath.Ticks(Min, Max, Count, MinStep);
    };

    /** The plot area: left room for the axis numbers, top room for the legend, bottom room for the categories. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.FrameOf - its gate, try/catch and handler label "kDocChartSvg.FrameOf" stay verbatim, so error reports are unchanged.
    public static FrameOf = (Self: kDocChartSvg): kDocChartFrame => {
        if (getCoreUtils().KErrorMode) { return new kDocChartFrame(); }
        try {
            const Out: kDocChartFrame = new kDocChartFrame();
            const Extent: number[] = Self.Extent();
            let MinStep: number = 0;
            if (Self["_Web"].IntegerTicks === true && Self.DataDecimals() === 0) { MinStep = 1; }
            Out.Ticks = kDocChartSvgMath.Ticks(Extent[0], Extent[1], 5, MinStep);
            Out.Min = Out.Ticks[0];
            Out.Max = Out.Ticks[Out.Ticks.length - 1];
            let Widest: number = 0;
            for (const Tick of Out.Ticks) { Widest = Math.max(Widest, Self.TickLabel(Tick, Out.Ticks).length); }
            const Size: number = Self.Size();
            Out.X = Widest * Size * 0.55 + Size * 0.6;
            Out.Y = Self.LegendHeight() + Self.LabelSize() * 1.4;
            Out.W = Math.max(1, Self["_W"] - Out.X);
            Out.H = Math.max(1, Self["_H"] - Out.Y - Size * 1.8);
            if (Self["_Web"].LabelHeadroom === true && Self.GroupLabels() === true) { Self.Refit(Out, MinStep); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.FrameOf");
            return new kDocChartFrame();
        }
    };

    /** The y of a value in the plot area. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.YOf (it owns the gate and handler label).
    public static YOf = (Self: typeof kDocChartSvg, Frame: kDocChartFrame, Value: number): number => { return kDocChartSvgMath.YOf(Frame, Value); };

    /** The legend's height: one row when there are 2+ series, else none. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.LegendHeight - its gate, try/catch and handler label "kDocChartSvg.LegendHeight" stay verbatim, so error reports are unchanged.
    public static LegendHeight = (Self: kDocChartSvg): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (Self["_Chart"].Series.length < 2) { return 0; }
            return Self.Size() * 2;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.LegendHeight");
            return 0;
        }
    };

    // ---- cartesian charts --------------------------------------------------------------------------------------

    /** Bars / stacks / lines with grid, axis, category labels, value labels and the legend. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawCartesian - its gate, try/catch and handler label "kDocChartSvg.DrawCartesian" stay verbatim, so error reports are unchanged.
    public static DrawCartesian = (Self: kDocChartSvg): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Frame: kDocChartFrame = Self.FrameOf();
            Self.DrawGrid(Frame);
            Self.DrawCategories(Frame);
            if (Self["_Chart"].Kind === "stacked") { Self.DrawStacks(Frame); }
            if (Self["_Chart"].Kind === "bar" || Self["_Chart"].Kind === "barline") { Self.DrawBars(Frame); }
            Self.DrawLines(Frame);
            Self.DrawLegend();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawCartesian");
        }
    };

    /** The grid lines and numbers at the ticks, and the axis rule at 0. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawGrid - its gate, try/catch and handler label "kDocChartSvg.DrawGrid" stay verbatim, so error reports are unchanged.
    public static DrawGrid = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Grid: number = Self.Or(Self["_Style"].GridRulePt, 0.25);
            const Muted: string = Self["_Theme"].MutedColor || "#999999";
            for (const Tick of Frame.Ticks) {
                const Y: number = kDocChartSvgMath.YOf(Frame, Tick);
                if (Tick !== 0) { Self["_Parts"].push(Self.Rule(Frame.X, Y, Frame.X + Frame.W, Y, Grid, Muted)); }
                Self["_Parts"].push(Self.Text(Frame.X - Self.Size() * 0.5, Y + Self.Size() * 0.35, Self.TickLabel(Tick, Frame.Ticks), "end", Self.Size(), Self.TextColor()));
            }
            const Zero: number = kDocChartSvgMath.YOf(Frame, 0);
            Self["_Parts"].push(Self.Rule(Frame.X, Zero, Frame.X + Frame.W, Zero, Self.Or(Self["_Style"].AxisRulePt, 0.5), Self.TextColor()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawGrid");
        }
    };

    /** A line element. ⚠ kDocChartWeb ClassColors: the muted class instead of the stroke colour. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Rule - its gate, try/catch and handler label "kDocChartSvg.Rule" stay verbatim, so error reports are unchanged.
    public static Rule = (Self: kDocChartSvg, X1: number, Y1: number, X2: number, Y2: number, Width: number, Color: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Paint: string = " stroke=\"" + Color + "\"";
            if (Self["_Web"].ClassColors === true) { Paint = " class=\"" + Self["_Web"].MutedClass + "\""; }
            return "<line x1=\"" + Self.N(X1) + "\" y1=\"" + Self.N(Y1) + "\" x2=\"" + Self.N(X2) + "\" y2=\""
                + Self.N(Y2) + "\"" + Paint + " stroke-width=\"" + kDocChartSvgMath.Num(Width) + "\"/>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Rule");
            return "";
        }
    };

    /** A category's slot: [left x, width]. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.SlotOf - its gate, try/catch and handler label "kDocChartSvg.SlotOf" stay verbatim, so error reports are unchanged.
    public static SlotOf = (Self: kDocChartSvg, Frame: kDocChartFrame, Index: number): number[] => {
        if (getCoreUtils().KErrorMode) { return [0, 0]; }
        try {
            const Width: number = Frame.W / Self["_Chart"].Categories.length;
            return [Frame.X + Index * Width, Width];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.SlotOf");
            return [0, 0];
        }
    };

    /** The category names centred under their slots (kDocChartWeb ThinCategories: every n-th, none touching). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawCategories - its gate, try/catch and handler label "kDocChartSvg.DrawCategories" stay verbatim, so error reports are unchanged.
    public static DrawCategories = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Y: number = Frame.Y + Frame.H + Self.Size() * 1.4;
            const Every: number = Self.CategoryStep(Frame);
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index = Index + Every) {
                const Slot: number[] = Self.SlotOf(Frame, Index);
                Self["_Parts"].push(Self.Text(Slot[0] + Slot[1] / 2, Y, Self["_Chart"].Categories[Index], "middle", Self.Size(), Self.TextColor()));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawCategories");
        }
    };

    /** 1 (every category), or with ThinCategories the smallest step at which the widest name fits its slots. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.CategoryStep - its gate, try/catch and handler label "kDocChartSvg.CategoryStep" stay verbatim, so error reports are unchanged.
    public static CategoryStep = (Self: kDocChartSvg, Frame: kDocChartFrame): number => {
        if (getCoreUtils().KErrorMode) { return 1; }
        try {
            if (Self["_Web"].ThinCategories !== true || Self["_Chart"].Categories.length === 0) { return 1; }
            let Widest: number = 0;
            for (const Name of Self["_Chart"].Categories) { Widest = Math.max(Widest, Name.length * Self.Size() * 0.55); }
            const Slot: number = Frame.W / Self["_Chart"].Categories.length;
            return Math.max(1, Math.ceil((Widest + Self.Size()) / Math.max(0.01, Slot)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.CategoryStep");
            return 1;
        }
    };

    /** The bar series (not AsLine in a barline): side by side in each slot. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawBars - its gate, try/catch and handler label "kDocChartSvg.DrawBars" stay verbatim, so error reports are unchanged.
    public static DrawBars = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Bars: number[] = Self.BarSeries();
            if (Bars.length === 0) { return; }
            const Gap: number = Self.Or(Self["_Style"].BarGapPct, 30) / 100;
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index++) {
                const Slot: number[] = Self.SlotOf(Frame, Index);
                const Width: number = Slot[1] * (1 - Gap) / Bars.length;
                const Left: number = Slot[0] + Slot[1] * Gap / 2;
                for (let K: number = 0; K < Bars.length; K++) { Self.DrawBar(Frame, Bars[K], Index, Left + K * Width, Width, 0, Self.ColumnOf(Slot[1], Bars.length)); }
            }
            if (Self.GroupLabels() === true) { Self.DrawPlacedLabels(Frame); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawBars");
        }
    };

    /** The column a bar's value label may use: the whole slot for a single bar series, else the bar itself (0). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.ColumnOf - its gate, try/catch and handler label "kDocChartSvg.ColumnOf" stay verbatim, so error reports are unchanged.
    public static ColumnOf = (Self: kDocChartSvg, Slot: number, Bars: number): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            if (Bars === 1) { return Slot; }
            return 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.ColumnOf");
            return 0;
        }
    };

    /** kDocChartWeb.LabelsAboveGroup is on and the bars really are grouped (2+ bar series, not stacked). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.GroupLabels - its gate, try/catch and handler label "kDocChartSvg.GroupLabels" stay verbatim, so error reports are unchanged.
    public static GroupLabels = (Self: kDocChartSvg): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Self["_Web"].LabelsAboveGroup !== true || Self["_Chart"].Kind === "stacked") { return false; }
            return Self.BarSeries().length > 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.GroupLabels");
            return false;
        }
    };

    /** One category's bar values, largest first (missing values left out). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.GroupValues - its gate, try/catch and handler label "kDocChartSvg.GroupValues" stay verbatim, so error reports are unchanged.
    public static GroupValues = (Self: kDocChartSvg, Index: number): number[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Bars: number[] = Self.BarSeries();
            const Out: number[] = [];
            for (let K: number = 0; K < Bars.length; K++) {
                const Value: number | null = Self["_Chart"].Series[Bars[K]].Values[Index];
                if (Value === null || Value === undefined || isNaN(Value)) { continue; }
                if (Self.LabelledValue(Bars[K], Index) !== true) { continue; }
                Out.push(Value);
            }
            Out.sort(kDocChartSvgMath.Descending);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.GroupValues");
            return [];
        }
    };

    /** A sort order: larger numbers first. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Descending (it owns the gate and handler label).
    public static Descending = (Self: typeof kDocChartSvg, A: number, B: number): number => { return kDocChartSvgMath.Descending(A, B); };

    /** One category's labels over its whole group (the placement DrawPlacedLabels uses for every category). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawGroupLabels - its gate, try/catch and handler label "kDocChartSvg.DrawGroupLabels" stay verbatim, so error reports are unchanged.
    public static DrawGroupLabels = (Self: kDocChartSvg, Frame: kDocChartFrame, Index: number, Slot: number[]): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const One of Self.PlaceLabels(Frame)) {
                if (One.Index !== Index || Slot.length < 2) { continue; }
                if (Self["_Web"].LabelHeadroom !== true && One.Y < Frame.Y) { continue; }
                Self["_Parts"].push(Self.Text(One.X, One.Y, One.Text, "middle", Self.LabelSize(), Self.TextColor()));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawGroupLabels");
        }
    };

    /** Every label over the groups, at one size; without LabelHeadroom one above the plot is left out. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawPlacedLabels - its gate, try/catch and handler label "kDocChartSvg.DrawPlacedLabels" stay verbatim, so error reports are unchanged.
    public static DrawPlacedLabels = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const One of Self.PlaceLabels(Frame)) {
                if (Self["_Web"].LabelHeadroom !== true && One.Y < Frame.Y) { continue; }
                Self["_Parts"].push(Self.Text(One.X, One.Y, One.Text, "middle", Self.LabelSize(), Self.TextColor()));
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawPlacedLabels");
        }
    };

    /**
     * The labels over the groups: each centred on its group (kept inside the width), its baseline above every bar its
     * text spans, then raised a line at a time until it touches no label placed before it (largest values first).
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.PlaceLabels - its gate, try/catch and handler label "kDocChartSvg.PlaceLabels" stay verbatim, so error reports are unchanged.
    public static PlaceLabels = (Self: kDocChartSvg, Frame: kDocChartFrame): kDocChartLabel[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Out: kDocChartLabel[] = [];
            const Size: number = Self.LabelSize();
            const Boxes: number[][] = Self.BarBoxes(Frame);
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index++) {
                const Slot: number[] = Self.SlotOf(Frame, Index);
                for (const Value of Self.GroupValues(Index)) {
                    const One: kDocChartLabel = new kDocChartLabel();
                    One.Index = Index;
                    One.Text = Self.Label(Value);
                    One.Half = One.Text.length * Size * 0.275;
                    One.X = Math.min(Self["_W"] - One.Half, Math.max(One.Half, Slot[0] + Slot[1] / 2));
                    One.Y = kDocChartSvgMath.TopIn(Boxes, One.X - One.Half, One.X + One.Half, Frame.Y + Frame.H) - Size * 0.4;
                    One.Y = kDocChartSvgMath.Clear(Out, One, Size * kDocChartSvgMath.LabelLine);
                    Out.push(One);
                }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.PlaceLabels");
            return [];
        }
    };

    /** Each drawn bar of the grouped layout as [left, right, top] (a negative bar's top is the zero line). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.BarBoxes - its gate, try/catch and handler label "kDocChartSvg.BarBoxes" stay verbatim, so error reports are unchanged.
    public static BarBoxes = (Self: kDocChartSvg, Frame: kDocChartFrame): number[][] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Out: number[][] = [];
            const Bars: number[] = Self.BarSeries();
            const Gap: number = Self.Or(Self["_Style"].BarGapPct, 30) / 100;
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index++) {
                const Slot: number[] = Self.SlotOf(Frame, Index);
                const Width: number = Slot[1] * (1 - Gap) / Math.max(1, Bars.length);
                const Left: number = Slot[0] + Slot[1] * Gap / 2;
                for (let K: number = 0; K < Bars.length; K++) {
                    const Value: number | null = Self["_Chart"].Series[Bars[K]].Values[Index];
                    if (Value === null || Value === undefined || isNaN(Value)) { continue; }
                    Out.push([Left + K * Width, Left + (K + 1) * Width, kDocChartSvgMath.YOf(Frame, Math.max(0, Value))]);
                }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.BarBoxes");
            return [];
        }
    };

    /** The highest bar top (smallest y) among Boxes that reach into Left..Right; Floor when none does. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.TopIn (it owns the gate and handler label).
    public static TopIn = (Self: typeof kDocChartSvg, Boxes: number[][], Left: number, Right: number, Floor: number): number => {
        return kDocChartSvgMath.TopIn(Boxes, Left, Right, Floor);
    };

    /** One's baseline raised by Line until it is clear of every label in Placed that it spans horizontally. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Clear (it owns the gate and handler label).
    public static Clear = (Self: typeof kDocChartSvg, Placed: kDocChartLabel[], One: kDocChartLabel, Line: number): number => {
        return kDocChartSvgMath.Clear(Placed, One, Line);
    };

    /** LabelHeadroom: the axis grows a nice tick at a time until the highest label's baseline is inside the plot. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.FitLabels - its gate, try/catch and handler label "kDocChartSvg.FitLabels" stay verbatim, so error reports are unchanged.
    public static FitLabels = (Self: kDocChartSvg, Frame: kDocChartFrame, MinStep: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (let Round: number = 0; Round < 8; Round++) {
                let Top: number = Frame.Y;
                for (const One of Self.PlaceLabels(Frame)) { Top = Math.min(Top, One.Y); }
                const Short: number = Frame.Y - Top;
                if (Short <= 0.01) { return; }
                const Room: number = Math.max(Frame.H * 0.25, Frame.H - Short);
                const Want: number = Frame.Min + (Frame.Max - Frame.Min) * Frame.H / Room;
                Frame.Ticks = kDocChartSvgMath.Ticks(Frame.Min, Math.max(Want, Frame.Max * 1.0001), 5, MinStep);
                Frame.Max = Frame.Ticks[Frame.Ticks.length - 1];
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.FitLabels");
        }
    };

    /** LabelHeadroom: fit the axis, give its (possibly wider) tick numbers their room on the left, and fit again. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Refit - its gate, try/catch and handler label "kDocChartSvg.Refit" stay verbatim, so error reports are unchanged.
    public static Refit = (Self: kDocChartSvg, Frame: kDocChartFrame, MinStep: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (let Round: number = 0; Round < 2; Round++) {
                Self.FitLabels(Frame, MinStep);
                let Widest: number = 0;
                for (const Tick of Frame.Ticks) { Widest = Math.max(Widest, Self.TickLabel(Tick, Frame.Ticks).length); }
                Frame.X = Widest * Self.Size() * 0.55 + Self.Size() * 0.6;
                Frame.W = Math.max(1, Self["_W"] - Frame.X);
            }
            Self.FitLabels(Frame, MinStep);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Refit");
        }
    };

    /** Value S at category I gets a label: ExtremesBySeries with "extremes" = that series' own extremes, else Labelled(I). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.LabelledValue - its gate, try/catch and handler label "kDocChartSvg.LabelledValue" stay verbatim, so error reports are unchanged.
    public static LabelledValue = (Self: kDocChartSvg, S: number, I: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Self["_Web"].ExtremesBySeries !== true || Self["_Chart"].ValueLabels !== "extremes") { return Self.Labelled(I); }
            return Self.SeriesExtremes(S).indexOf(I) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.LabelledValue");
            return false;
        }
    };

    /** A series' highest, lowest and last drawn value (categories with no value are never chosen). */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.SeriesExtremes - its gate, try/catch and handler label "kDocChartSvg.SeriesExtremes" stay verbatim, so error reports are unchanged.
    public static SeriesExtremes = (Self: kDocChartSvg, S: number): number[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Values: (number | null)[] = Self["_Chart"].Series[S].Values;
            let High: number = -1;
            let Low: number = -1;
            let Last: number = -1;
            for (let I: number = 0; I < Self["_Chart"].Categories.length; I++) {
                const Value: number | null = Values[I];
                if (Value === null || Value === undefined || isNaN(Value)) { continue; }
                if (High < 0 || Value > Number(Values[High])) { High = I; }
                if (Low < 0 || Value < Number(Values[Low])) { Low = I; }
                Last = I;
            }
            return [High, Low, Last].filter(kDocChartSvgMath.Found);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.SeriesExtremes");
            return [];
        }
    };

    /** An index that was found (not -1). */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Found (it owns the gate and handler label).
    public static Found = (Self: typeof kDocChartSvg, Index: number): boolean => { return kDocChartSvgMath.Found(Index); };
}
